import { ISpace, ISpaceUsage } from 'spw-redux/modules/floorplan/types';
import { isNil, always } from 'ramda';
import _, { sumBy, isFinite } from 'lodash';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import {
  IRoundBuilding,
  RoundsSummary,
  RoundVM,
} from 'spw-redux/modules/ou/types';

export const generateRoundsSummary = (
  rounds: RoundVM[],
  selectedBuildingId?: number,
  selectedFloorId?: number
): RoundsSummary => {
  const outputRounds: Omit<RoundVM, 'buildings'>[] = [];
  const buildingsMap: { [id: number]: IRoundBuilding } = {};
  let totalSpaces = 0;
  let completedSpaces = 0;
  let categoriesRelated: { [category: string]: boolean } = {};
  const copyOfRounds = JSON.parse(JSON.stringify(rounds)) as RoundVM[];
  copyOfRounds.forEach((r) => {
    categoriesRelated = {
      individualWorkspaces:
        r.categoriesRelated.individualWorkspaces ||
        categoriesRelated.individualWorkspaces,
      meetingSpaces:
        r.categoriesRelated.meetingSpaces || categoriesRelated.meetingSpaces,
      amenitySpaces:
        r.categoriesRelated.amenitySpaces || categoriesRelated.amenitySpaces,
      supportSpaces:
        r.categoriesRelated.supportSpaces || categoriesRelated.supportSpaces,
    };
    const round = { ...r };
    delete round.buildings;
    let roundTotalSpaces = 0;
    let roundCompletedSpaces = 0;

    let shouldRoundBeExcluded = true;
    if (r.buildings)
      r.buildings.forEach((building) => {
        if (buildingsMap[building.id]) {
          buildingsMap[building.id].floors = _.unionBy(
            buildingsMap[building.id].floors,
            building.floors,
            'id'
          );
        } else {
          buildingsMap[building.id] = building;
        }
        if (!selectedBuildingId || selectedBuildingId === building.id)
          building.floors.forEach((f) => {
            if (!selectedFloorId || f.id === selectedFloorId) {
              roundCompletedSpaces += f.completedSpaces;
              roundTotalSpaces += f.totalSpaces;
              shouldRoundBeExcluded = false;
            }
          });
      });

    totalSpaces += roundTotalSpaces;
    completedSpaces += roundCompletedSpaces;
    if (!shouldRoundBeExcluded) {
      outputRounds.push(round);
    }
  });
  const buildings = _.sortBy(Object.values(buildingsMap), 'index');
  const numberOfFloors = buildings.reduce(
    (acc, building) => acc + (building.floors?.length || 0),
    0
  );
  return {
    rounds: outputRounds,
    buildings,
    numberOfBuildings: buildings?.length,
    numberOfFloors,
    completedSpaces,
    totalSpaces,
    categoriesRelated,
  };
};

export const isSpaceUsageCompletedAndNotSkipped = (spaceUsage: ISpaceUsage) =>
  spaceUsage?.completed && spaceUsage?.status !== 'skipped_or_not_observed';

export const getExistingSeats = (space: ISpace) => {
  return space.seatCount;
};

export const countExistingSeats = (spaces: ISpace[]) =>
  sumBy(spaces, getExistingSeats);

export const getObservedSeats = (space: ISpace) => {
  if (
    !isNil(space.spaceUsage?.status) &&
    space.spaceUsage?.status !== 'skipped_or_not_observed'
  ) {
    return space.seatCount;
  }
  return null;
};

export const countObservedSeats = (spaces: ISpace[]) =>
  sumBy(spaces, getObservedSeats);

export const getUsedSeats = (space: ISpace) => {
  if (
    space.spaceUsage?.status !== 'skipped_or_not_observed' &&
    space.spaceUsage?.status !== 'not_in_use'
  ) {
    return space.spaceUsage?.seatsInUse;
  }

  if (space.spaceUsage?.status === 'not_in_use') {
    return 0;
  }

  return null;
};

export const countUsedSeats = (spaces: ISpace[]) => sumBy(spaces, getUsedSeats);

export const getObserverdSpace = (space: ISpace) => {
  if (isSpaceUsageCompletedAndNotSkipped(space?.spaceUsage)) {
    return 1;
  }

  return null;
};

export const getUsedSpace = (space: ISpace) => {
  if (
    isSpaceUsageCompletedAndNotSkipped(space?.spaceUsage) &&
    space.spaceUsage?.status !== 'not_in_use'
  ) {
    return 1;
  }

  if (space.spaceUsage?.status === 'not_in_use') {
    return 0;
  }

  return null;
};

export const areAllSpacesSkippedOrNotObserved = (spaces: ISpace[]) =>
  spaces.every(
    (space: ISpace) =>
      isNil(space.spaceUsage) ||
      space.spaceUsage.status === 'skipped_or_not_observed'
  );

export const getExistingSpace = always(1);

export const isSpaceCompleted = (space: ISpace) => space?.spaceUsage?.completed;

export const countObservedSpaces = (spaces: ISpace[]) =>
  spaces.filter((space) =>
    isSpaceUsageCompletedAndNotSkipped(space?.spaceUsage)
  ).length;

export const countSpacesWithUsage = (spaces: ISpace[]) =>
  sumBy(spaces, getUsedSpace);

export const countCompletedObservedSpaces = (spaces: ISpace[]) =>
  spaces.filter(isSpaceCompleted).length;

export const averageOccupancy = (spaces: ISpace[]) => {
  if (areAllSpacesSkippedOrNotObserved(spaces)) {
    return `${NO_DATA_TO_SHOW}%`;
  }

  const value = Math.round(
    (countSpacesWithUsage(spaces) / countObservedSpaces(spaces)) * 100
  );

  if (isFinite(value)) {
    return `${value}%`;
  }

  return `${NO_DATA_TO_SHOW}%`;
};

export const averageMeetingSize = (spaces: ISpace[]) => {
  if (areAllSpacesSkippedOrNotObserved(spaces)) {
    return NO_DATA_TO_SHOW;
  }

  const value = countUsedSeats(spaces) / countSpacesWithUsage(spaces);

  if (isFinite(value)) {
    return value.toFixed(1);
  }

  return Number(0).toFixed(1);
};
